import { useState, useEffect, useCallback } from 'react';

// Updated interface for the new simplified schema
export interface AgentSalesAnalytics {
  id: string;
  user_id: string;
  mls_profile_id: string;
  agent_mls_id: string;
  calculation_period: 'month' | 'quarter' | 'year' | 'ytd' | 'all_time';
  period_start_date: string;
  period_end_date: string;
  
  // Core metrics (simplified schema)
  total_transactions: number;
  total_volume: number;
  listings_sold: number;
  buyer_sales: number;
  average_sale_price: number;
  average_days_on_market: number;
  
  // Market analysis (JSON fields in new schema)
  geographic_breakdown?: Record<string, number>;
  price_range_analysis?: Record<string, number>;
  property_type_breakdown?: Record<string, number>;
  
  // Metadata
  records_processed: number;
  last_calculated: string;
  calculation_status: string;
  error_message?: string;
  
  // Agent profile info (from user_mls_agent_profiles join)
  agent_full_name?: string;
  mls_name?: string;
}

export interface AgentAnalyticsStats {
  id: string;
  title: string;
  emailCount: string | number;
  icon: any;
  iconColor: string;
  subtitle?: string;
}

export interface AgentAnalyticsResponse {
  analytics: AgentSalesAnalytics;
  stats: AgentAnalyticsStats[];
  period: string;
  lastUpdated: string;
}

export interface AgentSummaryResponse {
  agentInfo?: {
    mls_agent_id: string;
    agent_full_name: string;
    mls_name: string;
    verification_status: string;
  };
  availablePeriods: Array<{
    calculation_period: string;
    period_start_date: string;
    period_end_date: string;
    total_transactions: number;
    total_volume: number;
    last_calculated: string;
    calculation_status: string;
    records_processed: number;
  }>;
  hasData: boolean;
}

export const useAgentSalesAnalytics = (period: string = 'ytd') => {
  const [data, setData] = useState<AgentAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupRequired, setSetupRequired] = useState(false);

  const fetchAnalytics = useCallback(async (selectedPeriod: string = period) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // First check if agent profile exists
      console.log('🔍 Checking agent profile setup...');
      const statusResponse = await fetch('/api/onboarding/status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (statusResponse.ok) {
        const statusResult = await statusResponse.json();
        if (statusResult.success && statusResult.data?.agent_analytics?.setup_complete) {
          // Agent profile exists, proceed with analytics
          console.log('✅ Agent profile found, fetching analytics data');
          setSetupRequired(false);
        } else {
          // No agent profile, show setup
          console.log('❌ No agent profile found, showing setup');
          setSetupRequired(true);
          setData(null);
          setLoading(false);
          return;
        }
      }

      const response = await fetch(`/api/analytics/agent-sales?period=${selectedPeriod}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 404 && result.setupRequired) {
          setSetupRequired(true);
          setData(null);
          return;
        }
        throw new Error(result.message || 'Failed to fetch analytics');
      }

      setData(result.data || result);
      setSetupRequired(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching agent analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  const calculateAnalytics = async (selectedPeriod: string = period, force: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/analytics/agent-sales/calculate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ period: selectedPeriod, force }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to calculate analytics');
      }

      setData(result.data || result);
      return result.data || result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error calculating analytics:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const setupAgentMapping = async (agentData: {
    agentMlsId: string;
    agentFullName?: string;
    agentEmail?: string;
    mlsName?: string;
  }) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/analytics/agent-sales/setup', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mls_agent_id: agentData.agentMlsId,
          agent_full_name: agentData.agentFullName,
          agent_email: agentData.agentEmail,
          mls_name: agentData.mlsName || 'ARMLS'
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to setup agent mapping');
      }

      setSetupRequired(false);
      // Fetch analytics after setup
      await fetchAnalytics(period);
      
      return result.data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error setting up agent mapping:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics(period);
  }, [period, fetchAnalytics]);

  return {
    data,
    loading,
    error,
    setupRequired,
    fetchAnalytics,
    calculateAnalytics,
    setupAgentMapping,
    refetch: () => fetchAnalytics(period)
  };
};

export const useAgentSummary = () => {
  const [summary, setSummary] = useState<AgentSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/analytics/agent-sales/summary', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to fetch summary');
      }

      setSummary(result.data || result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching agent summary:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  return {
    summary,
    loading,
    error,
    refetch: fetchSummary
  };
}; 